import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A page state kept in the URL query (`?key=value`), so it survives a reload, the back button and a shared
 * link. The default value is left out of the URL. `replace` updates the current history entry instead of
 * adding one (for text typed letter by letter, or rows opened and closed).
 */
export function useUrlState<T extends string>(
  key: string, defaultValue: T, options: { allowed?: readonly T[]; replace?: boolean } = {},
): [T, (value: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(key);
  const value = raw !== null && (!options.allowed || (options.allowed as readonly string[]).includes(raw)) ? (raw as T) : defaultValue;
  const { replace } = options;
  const setValue = useCallback((next: T) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === defaultValue || next === '') params.delete(key);
      else params.set(key, next);
      return params;
    }, { replace });
  }, [key, defaultValue, replace, setSearchParams]);
  return [value, setValue];
}

/** A boolean flag in the URL: present as `?key=1` when true */
export function useUrlFlag(key: string): [boolean, (value: boolean) => void] {
  const [value, setValue] = useUrlState<'' | '1'>(key, '', { allowed: ['', '1'] });
  return [value === '1', useCallback((next: boolean) => setValue(next ? '1' : ''), [setValue])];
}

/**
 * A row opened from the URL (`?param=<id>`), one per list: a link can point at it, and it is scrolled
 * into view when the page opens on it. Opening and closing replace the history entry.
 */
export function useUrlRow<E extends HTMLElement>(param: string, id: string) {
  const [current, setCurrent] = useUrlState<string>(param, '', { replace: true });
  const open = current === id;
  const ref = useRef<E>(null);
  const openedFromUrl = useRef(open);
  useEffect(() => {
    if (openedFromUrl.current) ref.current?.scrollIntoView?.({ block: 'center' });
  }, []);
  const toggle = useCallback(() => setCurrent(open ? '' : id), [open, id, setCurrent]);
  return { open, toggle, ref };
}
